import { css } from '@emotion/react';
import isEqual from 'lodash/isEqual';
import {
  ElementType,
  Suspense,
  lazy,
  memo,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';

import type { SelectedAlert } from '~/@types/redux';
import Box from '~/common/components/Box';
import { useUser } from '~/common/hooks/useAuthentication';
import useBoltStatus from '~/common/hooks/useBoltStatus';
import useFullPageWithPDFViewer from '~/common/hooks/useFullPageWithPDFViewer';
import useIsWriteAccessDisabled from '~/common/hooks/useIsWriteAccessDisabled';
import { determineAutoCheck } from '~/common/util/underwriter';
import { HomeActions, HomeThunks } from '~/pages/Home/redux';
import { IncomeRuleIds } from '~/pages/IncomeCalculator/enums';
import { useSignalRIsConnected } from '~/stores/useSignalRStore.ts';

import Oops from '../Oops.tsx';
import Spinner from '../Spinner';
import { useAuditHistoryButtonStore } from './ActionBar/stores/useAuditHistoryButtonStore.ts';
import Content from './Content';
import Footer from './Footer';
import GlobalError from './GlobalError.tsx';
import Header from './Header';
import PageHeader from './PageHeader';
import { TOP_NAV_HEIGHT } from './PageLayout/Styled.ts';
import { signalRNavigationStatus } from './signalRNavigation';
import { Action, PrimaryAction } from './types.ts';

const ActionBar = lazy(() => import('./ActionBar/ActionBar.tsx'));

type GlobalLayoutProps = {
  children?: any;
  as?: ElementType;
  customHeader?: React.ReactNode;
  error?: string;
  showOops?: boolean;
  heading?: string;
  isLoading?: boolean;
  loadingMessage?: string;
  primaryButton?: PrimaryAction;
  secondaryButton?: Action & { name: string };
  subHeading?: string;
  customStyles?: {
    main?: string;
  };
  isDirty?: boolean;
  subHeadingStyles?: any;
  waitForSignalR?: boolean;
  auditHistoryData?: any;
  shouldAutoCheck?: boolean;
  globalLayoutName?: string;
  forceFullPage?: boolean;
  hasNewBackground?: boolean;
  contentProps?: React.ComponentProps<typeof Content>;
  actionButtonProps?: Partial<React.ComponentProps<typeof ActionBar>>;
  isHeaderHidden?: boolean;
  disableHeadingPadding?: boolean;
};

const renderHeader = (
  auditHistoryData: GlobalLayoutProps['auditHistoryData'],
  customHeader: React.ReactNode,
  heading: string | undefined,
  isWriteAccessDisabled: boolean,
) =>
  auditHistoryData ? (
    <PageHeader {...auditHistoryData} />
  ) : (
    (customHeader ?? (
      <Header heading={heading} isWriteAccessDisabled={isWriteAccessDisabled} />
    ))
  );

const getHeaderStyle = (isFullPageWithPDFViewer: boolean) =>
  isFullPageWithPDFViewer
    ? {
        position: 'sticky' as const,
        top: 0,
        zIndex: 10,
        backgroundColor: 'white',
      }
    : undefined;

const getContentBodyCss = (
  hasNewBackground: boolean,
  headerHeight: number,
) => css`
  --gl-header-height: ${headerHeight}px;
  ${hasNewBackground
    ? `
        *::-webkit-scrollbar,
        *::-webkit-scrollbar-track,
        *::-webkit-scrollbar-corner {
          background-color: #edf2f7 !important;
        }
        *::-webkit-scrollbar-thumb {
          border: 4px solid #edf2f7 !important;
        }
      `
    : ''}
`;

const getContentBodySx = ({
  isHeaderHidden,
  disableHeadingPadding,
  hasNewBackground,
  isFullPageWithPDFViewer,
  headerHeight,
  customStyles,
}: {
  isHeaderHidden: boolean;
  disableHeadingPadding: boolean;
  hasNewBackground: boolean;
  isFullPageWithPDFViewer: boolean;
  headerHeight: number;
  customStyles?: { main?: string };
}) => ({
  '--gl-header-height': `${headerHeight}px`,
  padding: isHeaderHidden || disableHeadingPadding ? undefined : '24px 32px',
  height: '100%',
  minHeight: `calc(100vh - ${TOP_NAV_HEIGHT} - var(--gl-header-height, 0px))`,
  overflowY: 'auto',
  backgroundColor: hasNewBackground ? 'gray.100' : 'white',
  ...(isFullPageWithPDFViewer
    ? {
        padding: '0',
        height: 'auto',
        overflowY: 'visible',
      }
    : {}),
  ...customStyles,
});

const getContentNode = ({
  showOops,
  error,
  subHeading,
  subHeadingStyles,
  isWriteAccessDisabled,
  contentProps,
  children,
}: {
  showOops: boolean;
  error?: string;
  subHeading?: string;
  subHeadingStyles?: any;
  isWriteAccessDisabled: boolean;
  contentProps: React.ComponentProps<typeof Content>;
  children?: React.ReactNode;
}) => {
  if (showOops) {
    return <Oops />;
  }

  if (error) {
    return <GlobalError error={error} />;
  }

  return (
    <Content
      subHeading={subHeading}
      subHeadingStyles={subHeadingStyles}
      isWriteAccessDisabled={isWriteAccessDisabled}
      {...contentProps}
    >
      {children}
    </Content>
  );
};

const normalizeSecondaryButton = (
  secondaryButton: GlobalLayoutProps['secondaryButton'],
) => {
  if (typeof secondaryButton !== 'object' || secondaryButton === null) {
    return undefined;
  }

  return {
    ...secondaryButton,
    isDisabled: secondaryButton.isDisabled,
  };
};

const GlobalLayout = ({
  as = 'div',
  children,
  customHeader = null,
  error,
  showOops = false,
  heading,
  isLoading = false,
  loadingMessage,
  primaryButton,
  secondaryButton,
  subHeading,
  customStyles,
  isDirty,
  subHeadingStyles,
  waitForSignalR = true,
  auditHistoryData = null,
  shouldAutoCheck = false,
  globalLayoutName = null,
  forceFullPage = false,
  hasNewBackground = false,
  contentProps = {},
  actionButtonProps = {},
  isHeaderHidden = false,
  disableHeadingPadding = false,
}: Readonly<GlobalLayoutProps>) => {
  const { selectedAlert } = useSelector((state) => state.home);
  const {
    data: { isLoanZapped },
  } = useBoltStatus();
  const { isUnderwriter, canWriteUnderwriterCheckbox } = useUser();
  const { FEVerified } = useSelector((state) => state.feVerified);
  const dispatch = useDispatch();
  const isSignalRConnected = useSignalRIsConnected();

  const shouldAutoCheckOrZap = determineAutoCheck(
    isLoanZapped,
    shouldAutoCheck,
    selectedAlert?.ruleId,
  );
  //TODO: remove once GlobalLayout has been refactored and SDUI has been implemented for selected page.
  const { isFullPageWithPDFViewer: originalIsFullPageWithPDFViewer } =
    useFullPageWithPDFViewer();
  const isFullPageWithPDFViewer =
    forceFullPage || originalIsFullPageWithPDFViewer;

  const isWriteAccessDisabled = useIsWriteAccessDisabled();

  // Measures the sticky header height so the PDF column's sticky top can
  // account for it via the --gl-header-height CSS custom property.
  const headerRef = useRef<HTMLDivElement>(null);
  const [headerHeight, setHeaderHeight] = useState(0);
  useEffect(() => {
    if (isHeaderHidden || !headerRef.current) {
      setHeaderHeight(0);
      return;
    }
    const el = headerRef.current;
    const observer = new ResizeObserver(([entry]) =>
      setHeaderHeight(entry.contentRect.height),
    );
    observer.observe(el);
    setHeaderHeight(el.getBoundingClientRect().height);
    return () => observer.disconnect();
  }, [isHeaderHidden]);

  type handlePrimaryButtonClickProps = {
    isUnderwriter?: boolean;
    shouldAutoCheckOrZap?: boolean;
    selectedAlert?: SelectedAlert;
    waitForSignalR?: boolean;
    FEVerified?: boolean;
    canWriteUnderwriterCheckbox?: boolean;
    isSignalRConnected: boolean;
  };

  // TODO: we should look to use the fn from defined in src/common/components/GlobalLayout/signalRNavigation.js
  const handlePrimaryButtonClick = ({
    isUnderwriter,
    shouldAutoCheckOrZap,
    selectedAlert,
    waitForSignalR = true,
    FEVerified,
    canWriteUnderwriterCheckbox,
    isSignalRConnected,
  }: Readonly<handlePrimaryButtonClickProps>) => {
    dispatch(HomeActions.setShouldGetNextAlert(true));

    const navigationStatus = signalRNavigationStatus(
      true, // response is truthy as you wont get here if form is not validated
      waitForSignalR,
      selectedAlert,
    );

    // Logging the SingalR status in console!
    // eslint-disable-next-line no-console
    console.log(navigationStatus);
    if (
      (isUnderwriter || canWriteUnderwriterCheckbox) &&
      selectedAlert &&
      selectedAlert?.additionalReferences?.ExcludeUnderwriterVerification !==
        'True'
    ) {
      if (
        selectedAlert.ruleId !==
        IncomeRuleIds.NoDocumentationProvidedIncomeSummary
      ) {
        const nextVerifiedValue = shouldAutoCheckOrZap || FEVerified;
        if (nextVerifiedValue !== selectedAlert.isUnderwriterVerified) {
          dispatch(
            HomeThunks.saveUnderwriterVerification(
              nextVerifiedValue,
              {} as SelectedAlert,
            ),
          );
        }
      }
    }

    // If !waitForSignalR, getNextAlert without waiting.
    if (!waitForSignalR) {
      dispatch(HomeActions.nextAlert(isUnderwriter));
      dispatch(HomeActions.setShouldGetNextAlert(false));
    }
    if (waitForSignalR && !isSignalRConnected) {
      // wait for 5 seconds and then getNextAlert if SignalR is not connected
      setTimeout(() => {
        dispatch(HomeActions?.nextAlert?.(isUnderwriter));
        dispatch(HomeActions?.setShouldGetNextAlert?.(false));
      }, 5000);
    }
  };

  useEffect(() => {
    const onBeforeUnload = (event) => {
      if (isDirty) {
        event.preventDefault();
        event.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [isDirty]);

  const submitHandler = async (event) => {
    if (!primaryButton?.isFormSubmit) {
      return;
    }

    event.preventDefault();

    if ('submit' in primaryButton) {
      const response = await primaryButton.submit();
      if (response) {
        handlePrimaryButtonClick({
          canWriteUnderwriterCheckbox,
          isUnderwriter,
          shouldAutoCheckOrZap,
          selectedAlert,
          waitForSignalR,
          FEVerified,
          isSignalRConnected,
        });
      }
      return;
    }

    const submitResult = primaryButton.handleSubmit(async (data) => {
      if (typeof primaryButton.onSubmit !== 'function') {
        return;
      }

      const response = await primaryButton.onSubmit(data);
      response &&
        handlePrimaryButtonClick({
          canWriteUnderwriterCheckbox,
          isUnderwriter,
          shouldAutoCheckOrZap,
          selectedAlert,
          waitForSignalR,
          FEVerified,
          isSignalRConnected,
        });
    });

    if (typeof submitResult === 'function') {
      await submitResult(event);
      return;
    }

    await submitResult;
  };

  const showSpinner = isLoading && !error;
  const shouldShowFooter = Boolean(primaryButton || secondaryButton);
  const normalizedSecondaryButton = normalizeSecondaryButton(secondaryButton);
  const headerStyle = getHeaderStyle(isFullPageWithPDFViewer);
  const contentBodyCss = getContentBodyCss(hasNewBackground, headerHeight);
  const contentBodySx = getContentBodySx({
    isHeaderHidden,
    disableHeadingPadding,
    hasNewBackground,
    isFullPageWithPDFViewer,
    headerHeight,
    customStyles,
  });
  const contentNode = getContentNode({
    showOops,
    error,
    subHeading,
    subHeadingStyles,
    isWriteAccessDisabled,
    contentProps,
    children,
  });

  if (primaryButton) {
    primaryButton.isDisabled =
      primaryButton.isDisabled || isWriteAccessDisabled;
  }

  return (
    <Box
      component={as}
      onSubmit={submitHandler}
      data-testid={globalLayoutName ?? `globalLayout-${as}`}
      sx={{
        borderTopRightRadius: '6px',
        borderBottomRightRadius: '6px',
        backgroundColor: 'white',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
    >
      <ActionBarAuditHistoryUpdater auditHistoryData={auditHistoryData} />
      {/* show page header if props else use customHeader or default */}
      {!isHeaderHidden && (
        <div
          ref={headerRef}
          data-testid="globalLayout-header"
          style={headerStyle}
        >
          {renderHeader(
            auditHistoryData,
            customHeader,
            heading,
            isWriteAccessDisabled,
          )}
        </div>
      )}
      {showSpinner ? (
        <Spinner loadingMessage={loadingMessage} />
      ) : (
        <Box
          className="content-body"
          data-testid="globalLayout-content-body"
          css={contentBodyCss}
          sx={contentBodySx}
        >
          {contentNode}
        </Box>
      )}

      {shouldShowFooter && (
        <Footer
          shouldAutoCheck={shouldAutoCheckOrZap}
          primaryButton={primaryButton}
          secondaryButton={normalizedSecondaryButton}
          error={error}
          waitForSignalR={waitForSignalR}
        />
      )}
      <Suspense fallback={null}>
        <ActionBar
          primaryAction={primaryButton}
          secondaryAction={secondaryButton}
          waitForSignalR={waitForSignalR}
          shouldAutoCheck={shouldAutoCheckOrZap}
          {...actionButtonProps}
        />
      </Suspense>
    </Box>
  );
};

type ActionBarAuditHistoryUpdaterProps = {
  auditHistoryData: GlobalLayoutProps['auditHistoryData'];
};

const ActionBarAuditHistoryUpdater = memo(
  function ActionBarAuditHistoryUpdater({
    auditHistoryData,
  }: ActionBarAuditHistoryUpdaterProps) {
    const setAuditHistoryDataForActionBar = useAuditHistoryButtonStore(
      (s) => s.setAuditHistoryData,
    );

    useEffect(() => {
      setAuditHistoryDataForActionBar(auditHistoryData);
    }, [setAuditHistoryDataForActionBar, auditHistoryData]);

    return null;
  },
  (prevProps, nextProps) =>
    isEqual(prevProps.auditHistoryData, nextProps.auditHistoryData),
);

export default GlobalLayout;
